import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { CloudUpload, Delete, InsertDriveFile } from '@mui/icons-material';
import { apiServices } from '../services';

// Flow status options with codes and full descriptions
const FLOW_STATUS_OPTIONS = [
  { code: 'P', label: 'Perennial' },
  { code: 'NP', label: 'Nonperennial, subtype not known' },
  { code: 'RFI', label: 'Regularly flowing intermittent' },
  { code: 'SFI', label: 'Seldom flowing intermittent' },
  { code: 'I', label: 'Intermittent, subtype not known' },
  { code: 'E', label: 'Ephemeral' }
];

// Helper function to get full label from code
const getFlowStatusLabel = (code) => {
  const option = FLOW_STATUS_OPTIONS.find(opt => opt.code === code);
  return option ? `${option.label} (${option.code})` : code || 'Unknown';
};

const FlowStatusForm = ({ 
  masterid, 
  flowstatusFinal,
  onSubmit, 
  onCancel,
  isSubmitting = false,
  verifiedEmail = ''
}) => {
  const [formData, setFormData] = useState({
    email: verifiedEmail,
    flowstatus_updated: '',
    flowstatus_feedback: '',
    flowstatus_photos: 'no'
  });
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.flowstatus_updated) {
      newErrors.flowstatus_updated = 'Please select a flow status';
    }
    if (!formData.flowstatus_feedback) {
      newErrors.flowstatus_feedback = 'Feedback is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      let photosLink = null;
      
      // Upload files as zip if any selected
      if (selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          // Zip files and upload to S3, returns URL
          photosLink = await apiServices.uploadFilesAsZip(selectedFiles, masterid, 'flowstatus');
        } catch (error) {
          console.error('Error uploading files:', error);
          setErrors(prev => ({ ...prev, upload: error.message || 'Failed to upload files. Please try again.' }));
          setUploadingFiles(false);
          return;
        }
        setUploadingFiles(false);
      }
      
      const submissionData = {
        masterid: masterid,
        email: formData.email,
        flowstatus_feedback_date: new Date().toISOString(),
        flowstatus_final: flowstatusFinal,
        flowstatus_updated: formData.flowstatus_updated,
        flowstatus_feedback: formData.flowstatus_feedback,
        flowstatus_photos: formData.flowstatus_photos,
        flowstatus_photos_link: photosLink,
        flowstatus_feedback_evaluator: null,
        flowstatus_feedback_evaluationdate: null
      };
      console.log('Flow Status Feedback Submission:', submissionData);
      onSubmit(submissionData);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" color="primary">
        Flow Status Feedback
      </Typography>

      {/* Auto-populated fields display */}
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Master ID:</strong> {masterid || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <strong>Current Flow Status:</strong> {getFlowStatusLabel(flowstatusFinal)}
        </Typography>
      </Box>

      {/* Email field */}
      <TextField
        label="Email"
        type="email"
        required
        fullWidth
        value={formData.email}
        onChange={handleChange('email')}
        error={!!errors.email}
        helperText={verifiedEmail ? 'Verified email address' : errors.email}
        placeholder="your.email@example.com"
        disabled={!!verifiedEmail}
        InputProps={{
          readOnly: !!verifiedEmail
        }}
        sx={verifiedEmail ? { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' } } : {}}
      />

      {/* Updated Flow Status Selection */}
      <FormControl fullWidth required error={!!errors.flowstatus_updated}>
        <InputLabel id="flowstatus-updated-label">
          Which flow status do you believe is most appropriate for this site?
        </InputLabel>
        <Select
          labelId="flowstatus-updated-label"
          value={formData.flowstatus_updated}
          onChange={handleChange('flowstatus_updated')}
          label="Which flow status do you believe is most appropriate for this site?"
        >
          {FLOW_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label} ({option.code})
            </MenuItem>
          ))}
        </Select>
        {errors.flowstatus_updated && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
            {errors.flowstatus_updated}
          </Typography>
        )}
      </FormControl>

      {/* Flow Status Feedback */}
      <TextField
        label="Flow Status Feedback"
        required
        fullWidth
        multiline
        rows={4}
        value={formData.flowstatus_feedback}
        onChange={handleChange('flowstatus_feedback')}
        error={!!errors.flowstatus_feedback}
        helperText={errors.flowstatus_feedback || "What information do you have about the flow status of this site?"}
        placeholder="Please describe any information you have about the flow status..."
      />

      {/* Photos/Data question */}
      <FormControl component="fieldset" required>
        <FormLabel component="legend">
          Do you have photos or data you'd like to share about the flow status of this site?
        </FormLabel>
        <RadioGroup
          row
          value={formData.flowstatus_photos}
          onChange={handleChange('flowstatus_photos')}
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      {formData.flowstatus_photos === 'yes' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept=".jpg,.jpeg,.png,.xls,.xlsx,.csv"
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={handleUploadClick}
            disabled={isSubmitting || uploadingFiles}
          >
            Select Files to Upload
          </Button>
          
          {selectedFiles.length > 0 && (
            <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
              {selectedFiles.map((file, index) => (
                <ListItem key={index}>
                  <InsertDriveFile sx={{ mr: 1, color: 'primary.main' }} />
                  <ListItemText 
                    primary={file.name} 
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      onClick={() => handleRemoveFile(index)}
                      disabled={isSubmitting || uploadingFiles}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {errors.upload && (
            <Alert severity="error">{errors.upload}</Alert>
          )}
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting || uploadingFiles}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting || uploadingFiles}
          startIcon={(isSubmitting || uploadingFiles) ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {uploadingFiles ? 'Uploading Files...' : isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </Box>
    </Box>
  );
};

export default FlowStatusForm;
